import { useCallback, useMemo, useState, useEffect } from "react";
import { IndustriesPayload, IndustryType } from "../types";
import { INDUSTRY_TYPES } from "../constants";

interface UseIndustrySelectionProps {
  selectedValues: IndustriesPayload;
  onValuesChange: (payload: IndustriesPayload) => void;
}

/**
 * Infers the industry type based on which arrays have values
 */
const inferIndustryType = (selectedValues: IndustriesPayload): IndustryType => {
  if (
    selectedValues.industryIds?.length > 0 ||
    selectedValues.excludedIds?.length
  ) {
    return INDUSTRY_TYPES.INDUSTRY;
  } else if (
    selectedValues.sicIds?.length > 0 ||
    selectedValues.excludedSicIds?.length
  ) {
    return INDUSTRY_TYPES.SIC;
  } else if (
    selectedValues.naicsIds?.length > 0 ||
    selectedValues.excludedNaicsIds?.length
  ) {
    return INDUSTRY_TYPES.NAICS;
  } else {
    return INDUSTRY_TYPES.INDUSTRY;
  }
};

/**
 * Custom hook to manage industry selection state and callbacks
 */
export function useIndustrySelection({
  selectedValues,
  onValuesChange,
}: UseIndustrySelectionProps) {
  // Compute the initial industry type
  const initialIndustryType = useMemo(() => {
    return (
      selectedValues.selectedIndustryType || inferIndustryType(selectedValues)
    );
  }, [selectedValues]);

  const [selectedIndustryType, setSelectedIndustryType] =
    useState<IndustryType>(initialIndustryType);
  const [userSelectedType, setUserSelectedType] = useState<IndustryType | null>(
    null
  );

  // Sync with external changes
  useEffect(() => {
    // Only update if selectedIndustryType is explicitly provided in selectedValues
    if (selectedValues.selectedIndustryType) {
      setSelectedIndustryType(selectedValues.selectedIndustryType);
      setUserSelectedType(selectedValues.selectedIndustryType);
    } else if (!userSelectedType) {
      // Only infer type if user hasn't explicitly selected one
      const inferredType = inferIndustryType(selectedValues);
      setSelectedIndustryType(inferredType);
    }
  }, [selectedValues, userSelectedType]);

  // Get current values based on industry type
  const industryIds = useMemo(
    () => selectedValues.industryIds || [],
    [selectedValues.industryIds]
  );
  const sicIds = useMemo(
    () => selectedValues.sicIds || [],
    [selectedValues.sicIds]
  );
  const naicsIds = useMemo(
    () => selectedValues.naicsIds || [],
    [selectedValues.naicsIds]
  );
  const excludedIds = useMemo(
    () => selectedValues.excludedIds || [],
    [selectedValues.excludedIds]
  );
  const excludedSicIds = useMemo(
    () => selectedValues.excludedSicIds || [],
    [selectedValues.excludedSicIds]
  );
  const excludedNaicsIds = useMemo(
    () => selectedValues.excludedNaicsIds || [],
    [selectedValues.excludedNaicsIds]
  );

  /**
   * Handle changing the industry type via toggle buttons
   */
  const handleIndustryTypeChange = useCallback(
    (_event: React.MouseEvent<HTMLElement>, industryType: IndustryType) => {
      if (!industryType) return;

      setSelectedIndustryType(industryType);
      setUserSelectedType(industryType);

      // Create payload with only the relevant fields for the selected type
      let payload: IndustriesPayload;

      switch (industryType) {
        case INDUSTRY_TYPES.INDUSTRY:
          payload = {
            selectedIndustryType: industryType,
            industryIds: selectedValues.industryIds || [],
            sicIds: [],
            naicsIds: [],
            excludedIds: selectedValues.excludedIds || [],
            excludedSicIds: [],
            excludedNaicsIds: [],
          };
          break;
        case INDUSTRY_TYPES.SIC:
          payload = {
            selectedIndustryType: industryType,
            industryIds: [],
            sicIds: selectedValues.sicIds || [],
            naicsIds: [],
            excludedIds: [],
            excludedSicIds: selectedValues.excludedSicIds || [],
            excludedNaicsIds: [],
          };
          break;
        case INDUSTRY_TYPES.NAICS:
          payload = {
            selectedIndustryType: industryType,
            industryIds: [],
            sicIds: [],
            naicsIds: selectedValues.naicsIds || [],
            excludedIds: [],
            excludedSicIds: [],
            excludedNaicsIds: selectedValues.excludedNaicsIds || [],
          };
          break;
        default:
          return;
      }

      onValuesChange(payload);
    },
    [selectedValues, onValuesChange]
  );

  /**
   * Handle HG Industries selection
   */
  const handleHgIndustriesSelect = useCallback(
    (newIndustryIds: number[]) => {
      const payload: IndustriesPayload = {
        selectedIndustryType: INDUSTRY_TYPES.INDUSTRY,
        industryIds: newIndustryIds,
        sicIds: [],
        naicsIds: [],
        excludedIds,
        excludedSicIds: [],
        excludedNaicsIds: [],
      };
      onValuesChange(payload);
    },
    [excludedIds, onValuesChange]
  );

  /**
   * Handle SIC selection
   */
  const handleSicSelect = useCallback(
    (newSicIds: string[]) => {
      const payload: IndustriesPayload = {
        selectedIndustryType: INDUSTRY_TYPES.SIC,
        industryIds: [],
        sicIds: newSicIds,
        naicsIds: [],
        excludedIds: [],
        excludedSicIds,
        excludedNaicsIds: [],
      };
      onValuesChange(payload);
    },
    [excludedSicIds, onValuesChange]
  );

  /**
   * Handle NAICS selection
   */
  const handleNaicsSelect = useCallback(
    (newNaicsIds: string[]) => {
      const payload: IndustriesPayload = {
        selectedIndustryType: INDUSTRY_TYPES.NAICS,
        industryIds: [],
        sicIds: [],
        naicsIds: newNaicsIds,
        excludedIds: [],
        excludedSicIds: [],
        excludedNaicsIds,
      };
      onValuesChange(payload);
    },
    [excludedNaicsIds, onValuesChange]
  );

  /**
   * Handle excluded HG Industries selection
   */
  const handleExcludedHgIndustriesSelect = useCallback(
    (newExcludedIds: number[]) => {
      const payload: IndustriesPayload = {
        selectedIndustryType: INDUSTRY_TYPES.INDUSTRY,
        industryIds,
        sicIds: [],
        naicsIds: [],
        excludedIds: newExcludedIds,
        excludedSicIds: [],
        excludedNaicsIds: [],
      };
      onValuesChange(payload);
    },
    [industryIds, onValuesChange]
  );

  /**
   * Handle excluded SIC selection
   */
  const handleExcludedSicSelect = useCallback(
    (newExcludedSicIds: string[]) => {
      const payload: IndustriesPayload = {
        selectedIndustryType: INDUSTRY_TYPES.SIC,
        industryIds: [],
        sicIds,
        naicsIds: [],
        excludedIds: [],
        excludedSicIds: newExcludedSicIds,
        excludedNaicsIds: [],
      };
      onValuesChange(payload);
    },
    [sicIds, onValuesChange]
  );

  /**
   * Handle excluded NAICS selection
   */
  const handleExcludedNaicsSelect = useCallback(
    (newExcludedNaicsIds: string[]) => {
      const payload: IndustriesPayload = {
        selectedIndustryType: INDUSTRY_TYPES.NAICS,
        industryIds: [],
        sicIds: [],
        naicsIds,
        excludedIds: [],
        excludedSicIds: [],
        excludedNaicsIds: newExcludedNaicsIds,
      };
      onValuesChange(payload);
    },
    [naicsIds, onValuesChange]
  );

  return {
    selectedIndustryType,
    industryIds,
    sicIds,
    naicsIds,
    excludedIds,
    excludedSicIds,
    excludedNaicsIds,
    handleIndustryTypeChange,
    handleHgIndustriesSelect,
    handleSicSelect,
    handleNaicsSelect,
    handleExcludedHgIndustriesSelect,
    handleExcludedSicSelect,
    handleExcludedNaicsSelect,
  };
}
